
import React, { useState, useEffect, useCallback } from 'react';
import { 
    Card, CardBody, Table, 
    Button
} from 'reactstrap';
import Skeleton from 'react-loading-skeleton';

interface UserHistorySectionProps {
    username: string;
}

interface TransferRecord {
    time: number;
    type: string;
    from: string;
    to: string;
    amount: string;
    currency: string;
    memo: string;
}

const PAGE_SIZE = 20;

const fmtDate = (timestamp: number) => {
    const d = new Date(timestamp * 1000);
    const day = d.toLocaleString('en-US', { month: 'short', day: 'numeric' });
    const time = d.toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
    return { day, time };
};

export const UserHistorySection: React.FC<UserHistorySectionProps> = ({ username }) => {
    const [history, setHistory] = useState<TransferRecord[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Filters
    const [typeFilter, setTypeFilter] = useState('transfer');
    const [daysFilter, setDaysFilter] = useState('30');
    const [dirFilter, setDirFilter] = useState('in'); // in, out
    const [coinFilter, setCoinFilter] = useState('all'); // all, STEEM, SBD
    
    // Pagination
    const [offset, setOffset] = useState(0);
    const [hasMore, setHasMore] = useState(true);

    const fetchHistory = useCallback(async (isNew = false) => {
        setIsLoading(true);
        try {
            const currentOffset = isNew ? 0 : offset;
            if (isNew) setOffset(0);

            // Calculate fromTime
            let fromTime = 0;
            if (daysFilter !== 'all') {
                fromTime = Math.floor(Date.now() / 1000) - (parseInt(daysFilter) * 24 * 60 * 60);
            }

            // Build SDS Query
            const query: any = {};
            
            // Direction handling
            if (dirFilter === 'in') {
                query.to = username;
            } else {
                query.from = username;
            }

            if (typeFilter !== 'all') query.type = typeFilter;
            if (coinFilter !== 'all') query.unit = coinFilter;
            if (fromTime > 0) query.fromTime = fromTime;

            const queryStr = JSON.stringify(query);
            const url = `https://sds1.steemworld.org/transfers_api/getTransfers/${queryStr}/${PAGE_SIZE}/${currentOffset}`;
            
            const response = await fetch(url);
            const data = await response.json();
            
            if (data && data.result && data.result.rows) {
                const rows = data.result.rows.map((r: any) => ({
                    time: r[0],
                    type: r[1],
                    from: r[1],
                    to: r[2],
                    amount: r[3],
                    currency: r[4],
                    memo: r[5]
                }));
                
                setHistory(rows);
                setHasMore(rows.length === PAGE_SIZE);
            } else {
                setHistory([]);
                setHasMore(false);
            }
        } catch (error) {
            console.error("Failed to fetch history from SDS:", error);
        } finally {
            setIsLoading(false);
        }
    }, [username, typeFilter, daysFilter, dirFilter, coinFilter, offset]);

    useEffect(() => {
        fetchHistory(true);
    }, [typeFilter, daysFilter, dirFilter, coinFilter, username]);

    useEffect(() => {
        if (offset !== 0) {
            fetchHistory();
        }
    }, [offset]);

    const handlePageChange = (direction: 'next' | 'prev') => {
        const newOffset = direction === 'next' ? offset + PAGE_SIZE : Math.max(0, offset - PAGE_SIZE);
        setOffset(newOffset);
    };

    return (
        <div className="col-12 p-2">
            <Card className="item-speem mt-2 history-section shadow-none">
                <CardBody className="p-0">
                    <div className="p-3 border-bottom d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <h4 className="mb-0"><i className="fas fa-history mr-2" /> Wallet History</h4>
                        
                        <div className="history-filters d-flex flex-wrap gap-2">
                            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="videos-sort-input">
                                <option value="transfer">Transfers</option>
                                <option value="transfer_to_vesting">Power Up</option>
                                <option value="transfer_to_savings">To Savings</option>
                                <option value="transfer_from_savings">From Savings</option>
                            </select>
                            
                            <select value={dirFilter} onChange={(e) => setDirFilter(e.target.value)} className="videos-sort-input">
                                <option value="in">Received</option>
                                <option value="out">Sent</option>
                            </select>

                            <select value={coinFilter} onChange={(e) => setCoinFilter(e.target.value)} className="videos-sort-input">
                                <option value="all">Any Coin</option>
                                <option value="STEEM">STEEM</option>
                                <option value="SBD">SBD</option>
                            </select>

                            <select value={daysFilter} onChange={(e) => setDaysFilter(e.target.value)} className="videos-sort-input">
                                <option value="7">7d</option>
                                <option value="30">30d</option>
                                <option value="90">90d</option>
                                <option value="all">All</option>
                            </select>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <Table className="align-items-center table-flush history-table" responsive>
                            <thead className="thead-light">
                                <tr>
                                    <th>Date</th>
                                    <th>From / To</th>
                                    <th>Amount</th>
                                    <th>Memo / Note</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isLoading && !history.length ? (
                                    <>
                                        {[...Array(6)].map((_, i) => (
                                            <tr key={`skeleton-${i}`}>
                                                <td><Skeleton width={80} /><br /><Skeleton width={40} /></td>
                                                <td><Skeleton width={120} /><br /><Skeleton width={100} /></td>
                                                <td><Skeleton width={70} /><br /><Skeleton width={50} /></td>
                                                <td><Skeleton count={1} /></td>
                                            </tr>
                                        ))}
                                    </>
                                ) : history.length === 0 ? (
                                    <tr>
                                        <td colSpan={4} className="text-center py-5 text-muted">No transactions found for this selection.</td>
                                    </tr>
                                ) : (
                                    history.map((record, idx) => {
                                        const { day, time } = fmtDate(record.time);
                                        return (
                                            <tr key={`${record.time}-${idx}`}>
                                                <td className="small" style={{minWidth: "120px"}}>
                                                    <div className="font-weight-bold">{day}</div>
                                                    <div className="text-muted small">{time}</div>
                                                </td>
                                                <td>
                                                    <div className="d-flex flex-column" style={{minWidth: "140px"}}>
                                                        <span className={`small ${record.from === username ? 'fw-bold' : 'text-muted'}`}>
                                                            <i className="fas fa-arrow-right-from-bracket mr-1 opacity-5" /> @{record.from}
                                                        </span>
                                                        <span className={`small ${record.to === username ? 'fw-bold' : 'text-muted'}`}>
                                                            <i className="fas fa-arrow-right-to-bracket mr-1 opacity-5" /> @{record.to}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={{minWidth: "130px"}}>
                                                    <div className={`fw-bold ${record.to === username ? 'text-success' : 'text-danger'}`}>
                                                        {record.to === username ? '+' : '-'} {record.amount}
                                                    </div>
                                                    <div className="text-muted x-small uppercase">{record.currency}</div>
                                                </td>
                                                <td className="small text-muted">
                                                    <div className="memo-cell" style={{ maxWidth: '300px', whiteSpace: 'normal', height: 'auto' }}>
                                                        {record.memo || <span className="opacity-3">No memo provided</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="p-3 d-flex justify-content-between align-items-center border-top">
                        <span className="small text-muted">
                            Showing <strong>{offset + 1} - {offset + history.length}</strong> items
                        </span>
                        <div className="d-flex gap-2">
                            <Button 
                                color="speem" 
                                outline 
                                size="sm" 
                                disabled={offset === 0 || isLoading} 
                                onClick={() => handlePageChange('prev')}
                            >
                                <i className="fas fa-chevron-left mr-1" /> Previous
                            </Button>
                            <Button 
                                color="speem" 
                                outline 
                                size="sm" 
                                disabled={!hasMore || isLoading} 
                                onClick={() => handlePageChange('next')}
                            >
                                Next <i className="fas fa-chevron-right ml-1" />
                            </Button>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </div>
    );
};
